import { MapPin, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

const attractions = [
  {
    name: "DAYTONA BEACH BOARDWALK",
    category: "Entertainment",
    distance: "0.5 mi",
    description: "The iconic boardwalk and pier with arcades, rides, and restaurants. A classic Daytona experience, especially at sunset when the whole sky turns into a painting.",
    link: "https://www.daytonabeach.com/listing/daytona-beach-boardwalk-%26-pier/1147/",
  },
  {
    name: "PONCE DE LEON INLET LIGHTHOUSE",
    category: "History",
    distance: "12 mi",
    description: "The tallest lighthouse in Florida. Climb 203 steps for panoramic views that'll make your phone camera cry with joy. Worth every single step.",
    link: "https://www.ponceinlet.org/",
  },
  {
    name: "MARINE SCIENCE CENTER",
    category: "Nature",
    distance: "11 mi",
    description: "Sea turtle rehabilitation, stingray touch pools, and bird sanctuary. The kind of place that makes you feel good about humanity for a few hours.",
    link: "https://www.marinesciencecenter.com/",
  },
  {
    name: "DAYTONA INTERNATIONAL SPEEDWAY",
    category: "Sports",
    distance: "5 mi",
    description: "Home of the Daytona 500. Even if you're not into racing, the scale of this place is mind-blowing. Tours available year-round.",
    link: "https://www.daytonainternationalspeedway.com/",
  },
  {
    name: "ANGELL & PHELPS CHOCOLATE FACTORY",
    category: "Food",
    distance: "2 mi",
    description: "Handmade chocolate since 1925. Free tours and tastings. This is not a drill. Free. Chocolate. Tastings.",
    link: "https://www.angellandphelps.com/",
  },
  {
    name: "CICI AND HYATT BROWN MUSEUM OF ART",
    category: "Art",
    distance: "3 mi",
    description: "The largest collection of Florida art in the world. If you love our vibe at Seas The Bay, you'll love this place even more.",
    link: "https://moa.daytonastate.edu/",
  },
  {
    name: "NEW SMYRNA BEACH",
    category: "Beach",
    distance: "15 mi",
    description: "A quieter, more bohemian beach town just south. Great surfing, art galleries, and the kind of small-town charm that makes you consider relocating.",
    link: "https://www.visitnsbfl.com/",
  },
  {
    name: "ST. AUGUSTINE",
    category: "Day Trip",
    distance: "55 mi",
    description: "America's oldest city is just an hour north. Spanish colonial architecture, the Castillo fort, and more history than you can shake a selfie stick at.",
    link: "https://www.visitstaugustine.com/",
  },
];

const categoryColors = {
  Entertainment: "bg-coral text-white",
  History: "bg-ink text-sand",
  Nature: "bg-teal text-white",
  Sports: "bg-ink text-teal",
  Food: "bg-coral text-white",
  Art: "bg-teal text-ink",
  Beach: "bg-ink text-sand",
  "Day Trip": "bg-coral text-white",
};

export default function AttractionsPage() {
  return (
    <div data-testid="attractions-page">
      {/* Hero */}
      <section className="relative bg-ink py-20">
        <div className="noise-overlay absolute inset-0" />
        <div className="relative z-10 mx-auto max-w-7xl px-6">
          <p className="font-marker text-teal text-sm">Things to do</p>
          <h1 className="mt-2 font-anton text-4xl tracking-wider text-sand stencil-text sm:text-6xl">
            LOCAL ATTRACTIONS
          </h1>
          <div className="mt-2 h-1 w-20 bg-coral" />
          <p className="mt-4 font-courier text-sm text-sand/70 max-w-lg">
            Daytona Beach is more than just a beach (though the beach is pretty great).
            Here's our curated list of must-visits in the area.
          </p>
        </div>
      </section>

      {/* Attractions Grid */}
      <section className="bg-sand py-16">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid gap-8 md:grid-cols-2">
            {attractions.map((attr, i) => (
              <div
                key={i}
                data-testid={`attraction-card-${i}`}
                className="card-brutal bg-white p-8"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <span
                      className={`inline-block px-3 py-1 font-outfit text-xs tracking-wider uppercase ${
                        categoryColors[attr.category] || "bg-ink text-sand"
                      }`}
                    >
                      {attr.category}
                    </span>
                    <h3 className="mt-3 font-anton text-lg tracking-wider text-ink">
                      {attr.name}
                    </h3>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0 text-ink/50">
                    <MapPin className="h-3 w-3" />
                    <span className="font-outfit text-xs">{attr.distance}</span>
                  </div>
                </div>

                <p className="mt-4 font-courier text-sm leading-relaxed text-ink/70">
                  {attr.description}
                </p>

                <a
                  href={attr.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`attraction-link-${i}`}
                  className="mt-4 inline-flex items-center gap-1 font-outfit text-xs text-teal tracking-wider uppercase hover:underline"
                >
                  VISIT WEBSITE <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tips */}
      <section className="bg-ink py-16">
        <div className="noise-overlay absolute inset-0" />
        <div className="relative z-10 mx-auto max-w-7xl px-6">
          <h2 className="font-anton text-2xl tracking-wider text-sand stencil-text sm:text-3xl">
            INSIDER TIPS
          </h2>
          <div className="mt-2 h-1 w-16 bg-teal" />
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[
              "Drive on the beach — yes, you literally can. It's a Daytona thing.",
              "Sunrise on the east coast hits different. Set an alarm. Trust us.",
              "The best fish tacos are always at the place that looks least likely to have good fish tacos.",
            ].map((tip, i) => (
              <div key={i} className="border border-sand/20 p-6">
                <span className="font-marker text-coral text-3xl">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <p className="mt-2 font-courier text-sm text-sand/80 leading-relaxed">
                  {tip}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
